import type { DocumentType } from '../../types/tuition-assistance.ts';

/**
 * Shared asset↔bucket matching for the categorized-upload feature.
 *
 * The upload rows and the guided wizard's submission gate must read the SAME
 * bucket membership: when they diverged, the gate blocked submission on assets
 * no row rendered, with nothing actionable on screen (tuitionio/tio-front-end#9760).
 */

/**
 * The subset of TasAsset the matching reads. Structural so the wizard can pass
 * live records and unit tests can pass plain objects.
 */
export interface CategorizedUploadAssetLike {
  attachmentDeletedAt: string | null;
  classification: string | null;
  uploadRowKey: string | null;
  employeeSelectedType: DocumentType | null;
}

/** One admin-configured attachment row (from `documentEntriesFor`). */
export interface DocumentEntryLike {
  label: string;
  type: DocumentType;
}

/** Live = still attached (not discarded) and filed under this step's classification. */
export function isLiveForClassification(
  asset: CategorizedUploadAssetLike,
  classification: string
): boolean {
  return asset.attachmentDeletedAt === null && asset.classification === classification;
}

/**
 * Row-display membership. An asset tagged with an upload row key belongs only
 * to the row it was filed under; untagged (legacy) assets match by declared
 * type. Callers filter for liveness separately (the discarded tray needs
 * non-live assets).
 */
export function assetMatchesDocumentEntry(
  asset: CategorizedUploadAssetLike,
  entry: DocumentEntryLike
): boolean {
  if (asset.uploadRowKey) return asset.uploadRowKey === entry.label;
  return asset.employeeSelectedType === entry.type;
}

/**
 * True when a live upload under this classification carries neither a row key
 * nor a declared type — i.e. it predates the template's categorized rows (or
 * came from the legacy free-form uploader) and therefore can never satisfy a
 * configured entry.
 *
 * Callers on long-lived steps use this as a carryover escape hatch: an
 * application can sit in `ATTEND` for months, so a template that grows document
 * rows mid-flight must not strand evidence the employee already uploaded and
 * cannot re-file (the #9760 failure mode — a gate with nothing actionable on
 * screen).
 */
export function hasUntaggedLegacyUpload(
  assets: readonly CategorizedUploadAssetLike[],
  classification: string
): boolean {
  return assets.some(
    (asset) =>
      isLiveForClassification(asset, classification) &&
      !asset.uploadRowKey &&
      !asset.employeeSelectedType
  );
}

/**
 * The collection form of `hasUntaggedLegacyUpload`, for surfaces that need to
 * SHOW the carryover population rather than just gate on it: live uploads
 * under this classification that no categorized row can place (tio-front-end
 * #10226 — legacy-surface uploads were invisible on categorized surfaces).
 */
export function untaggedLegacyUploads<T extends CategorizedUploadAssetLike>(
  assets: readonly T[],
  classification: string
): T[] {
  return assets.filter(
    (asset) =>
      isLiveForClassification(asset, classification) &&
      !asset.uploadRowKey &&
      !asset.employeeSelectedType
  );
}

/**
 * Submission-gate presence check: every configured document ROW has at least one
 * live upload.
 *
 * Per row, not per unique document TYPE. `documentEntriesFor` resolves any label
 * the admin did not explicitly map to the fallback type `general`, so a template
 * configured with three unmapped rows produces three entries that share one
 * type. Keying the gate on the type set let a single upload satisfy all three:
 * the counter beside it read "1 / 3 required" and Submit Evidence was enabled
 * anyway, with two empty required rows on screen (#10426).
 *
 * A row counts as satisfied when `assetMatchesDocumentEntry` — the SAME
 * predicate `stateFor` uses to decide which files render in which row, and
 * therefore what `uploadedCount` counts — matches a live asset to it. Sharing
 * one predicate is the point: the gate cannot be stricter than what the employee
 * sees. Deriving an independent assignment here (one asset ↔ one row) made
 * Submit unreachable for an asset carrying a declared type but no row key, which
 * the display legitimately renders in EVERY row of that type.
 *
 * One narrow addition on top of display parity: an asset whose `uploadRowKey`
 * points at a label that no longer exists (a renamed row) matches no row, so the
 * display shows it only as prior-upload history — and unlike an untagged file it
 * cannot be re-filed from the Other-documents tray. It therefore falls back to
 * its declared type so a mid-flight template rename cannot strand an application
 * parked in ATTEND (#9760). That clause only ever permits, never blocks.
 *
 * AI validation verdicts are intentionally not consulted: they are advisory
 * for reviewers and must never gate submission. Verdict tags can be orphaned
 * at `pending` when no browser session sees the AI response (tio-api#7874),
 * which hard-blocked employees when the gate keyed off them (#9760).
 *
 * Vacuously true for an empty entry list — callers decide whether an
 * unconfigured step falls back to a legacy presence check.
 */
export function hasRequiredCategorizedUploads(
  assets: readonly CategorizedUploadAssetLike[],
  classification: string,
  entries: readonly DocumentEntryLike[]
): boolean {
  const configuredLabels = new Set(entries.map((entry) => entry.label));
  const live = assets.filter((asset) => isLiveForClassification(asset, classification));
  // Filed under a row the template no longer defines — see the rename carve-out.
  const orphanedByRename = live.filter(
    (asset) => asset.uploadRowKey && !configuredLabels.has(asset.uploadRowKey)
  );

  return entries.every(
    (entry) =>
      live.some((asset) => assetMatchesDocumentEntry(asset, entry)) ||
      orphanedByRename.some((asset) => asset.employeeSelectedType === entry.type)
  );
}
